// Compute Similar / Coordinating / Variation palettes for a picked color.
// Pure client-side math over a candidate pool; no AI, no server round-trip.
//
// - Similar: closest overall color (small LAB-ish distance).
// - Coordinating: complementary + analogous hues at usable brightness.
// - Variations: same hue family, spread across LRV (a light-to-dark ladder).

import type { SelectedColor } from "@/components/colors/ColorPicker";

export type ColorLite = Pick<
  SelectedColor,
  "id" | "brand" | "collection" | "color_number" | "color_name" | "hex" | "red" | "green" | "blue" | "lrv" | "swatch_url"
>;

export type RelatedGroups = {
  similar: ColorLite[];
  coordinating: ColorLite[];
  variations: ColorLite[];
};

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  const R = r / 255, G = g / 255, B = b / 255;
  const max = Math.max(R, G, B), min = Math.min(R, G, B);
  const l = (max + min) / 2;
  let h = 0, s = 0;
  const d = max - min;
  if (d !== 0) {
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case R: h = ((G - B) / d + (G < B ? 6 : 0)); break;
      case G: h = ((B - R) / d + 2); break;
      default: h = ((R - G) / d + 4); break;
    }
    h *= 60;
  }
  return [h, s, l];
}

function hueDist(a: number, b: number): number {
  const d = Math.abs(a - b) % 360;
  return d > 180 ? 360 - d : d;
}

function colorDistance(a: ColorLite, b: ColorLite): number {
  const dr = a.red - b.red, dg = a.green - b.green, db = a.blue - b.blue;
  return Math.sqrt(dr * dr + dg * dg + db * db);
}

export function computeRelated(
  seed: ColorLite,
  pool: ColorLite[],
): RelatedGroups {
  const candidates = pool.filter((c) => c.id !== seed.id);
  const [sh] = rgbToHsl(seed.red, seed.green, seed.blue);

  // Similar: smallest RGB distance (top 6).
  const similar = [...candidates]
    .sort((a, b) => colorDistance(seed, a) - colorDistance(seed, b))
    .slice(0, 6);

  // Coordinating: analogous (±25°) + complementary (180°±25°), reasonable saturation.
  const compHue = (sh + 180) % 360;
  const coordinating = candidates
    .map((c) => {
      const [ch, csat] = rgbToHsl(c.red, c.green, c.blue);
      const dAnalog = hueDist(ch, sh);
      const dComp = hueDist(ch, compHue);
      // prefer analogous OR complementary, penalize desaturated matches
      const hueScore = Math.min(dAnalog + 15, dComp);
      return { c, score: hueScore - csat * 20 };
    })
    .filter(({ c }) => {
      const [, csat] = rgbToHsl(c.red, c.green, c.blue);
      return csat > 0.08; // skip near-grays for coordinating picks
    })
    .sort((a, b) => a.score - b.score)
    .slice(0, 6)
    .map(({ c }) => c);

  // Variations: same hue (±20°), spread across brightness bands using LRV or lightness.
  const sameHue = candidates.filter((c) => {
    const [ch] = rgbToHsl(c.red, c.green, c.blue);
    return hueDist(ch, sh) < 25;
  });
  const withBrightness = sameHue.map((c) => {
    const [, , cl] = rgbToHsl(c.red, c.green, c.blue);
    const brightness = c.lrv != null ? c.lrv / 100 : cl;
    return { c, brightness };
  });
  // Bucket into 6 brightness bands, pick the closest to each band center.
  const targets = [0.05, 0.22, 0.4, 0.6, 0.78, 0.95];
  const seen = new Set<string>();
  const variations: ColorLite[] = [];
  for (const t of targets) {
    let best: (typeof withBrightness)[number] | null = null;
    let bestDist = Infinity;
    for (const item of withBrightness) {
      if (seen.has(item.c.id)) continue;
      const d = Math.abs(item.brightness - t);
      if (d < bestDist) { bestDist = d; best = item; }
    }
    if (best) { variations.push(best.c); seen.add(best.c.id); }
  }
  return { similar, coordinating, variations };
}

export function readableTextOn(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 145 ? "#0a0a0a" : "#ffffff";
}
